import { getSessionToken } from "@descope/react-sdk";
import { SessionHandler } from "verdi-web-client";
import { ApiError } from "./api-error";

// Matches UTC (`Z`) and offset (`+05:00`) datetime strings
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

function convertDateStringToDate(_key: string, value: unknown): unknown {
    if (typeof value === "string" && ISO_DATE_RE.test(value)) {
        return new Date(value);
    }
    return value;
}

// Dynamic access to avoid esbuild CJS warning during Orval generation
// At runtime, Vite properly provides import.meta.env
const getEnv = () => import.meta.env;

/**
 * Custom fetch mutator for Orval-generated API clients.
 *
 * Returns the parsed body rather than Orval's `{ data, status, headers }` envelope, which is what
 * `includeHttpResponseReturnType: false` in orval-config declares. The `as T` casts below are
 * unchecked, so changing one side without the other breaks every call site silently.
 *
 * Throws `ApiError` on non-2xx responses so React Query enters its error state.
 */
export async function customFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
    // Derive v2 base from the shared backend URL
    const baseURLV2 = `${(getEnv().VITE_BACKEND_URL ?? "").replace(/\/+$/, "")}/v2`;
    const path = url.startsWith("/") ? url : `/${url}`;
    const fullURL = `${baseURLV2}${path}`;

    const sessionToken = getSessionToken();

    // The User header lets admin users specify which account they're acting as
    const headers = new Headers(options.headers ?? {});

    if (sessionToken) {
        headers.set("Authorization", `Bearer ${sessionToken}`);
    }

    if (SessionHandler.curUser) {
        headers.set("User", SessionHandler.curUser);
    }

    const res = await fetch(fullURL, {
        ...options,
        // Preserve orval's query cancellation signal but fall back to a 30s timeout if not provided
        signal: options.signal ?? AbortSignal.timeout(30_000),
        headers,
    });

    const ct = res.headers.get("content-type") ?? "";

    if (res.status === 204) {
        return undefined as T;
    }

    if (ct.includes("application/json")) {
        const text = await res.text();
        const parsed = JSON.parse(text, convertDateStringToDate) as { message: string };

        if (!res.ok) {
            throw new ApiError(res.status, parsed);
        }

        return parsed as T;
    }

    // Non-JSON response — plain text or unexpected content type
    const text = await res.text().catch(() => "");

    if (!res.ok) {
        throw new ApiError(res.status, { message: `${res.statusText}${text ? ` - ${text}` : ""}` });
    }

    return text as unknown as T;
}
